import React from 'react';
import { c, radius, t } from '../theme';

// Turns whatever the auth backend threw into something a pharmacy owner can
// act on.
//
// Supabase messages are written for developers: "Invalid login credentials",
// "AuthApiError: User already registered", "For security purposes, you can only
// request this after 24 seconds". Passing those straight to the screen is what
// makes an app feel like it is leaking its own internals at you.
//
// Matching is on lowercased substrings because Supabase changes the exact
// wording between versions, and anything unrecognised falls through to the raw
// message rather than a useless generic — a developer message the person can
// screenshot beats "Something went wrong" with no detail at all.
export function humanAuthError(err) {
  const raw = (err && (err.message || String(err))) || '';
  const m = raw.toLowerCase();

  if (m.includes('invalid login credentials') || m.includes('invalid_credentials')) {
    return {
      title: 'Email or password is incorrect',
      detail: 'Check both and try again. Passwords are case-sensitive.',
    };
  }
  if (m.includes('email not confirmed')) {
    return {
      title: 'Email not verified yet',
      detail: 'Open the verification link we sent you, then sign in.',
    };
  }
  if (m.includes('user already registered') || m.includes('already been registered')) {
    return {
      title: 'That email already has an account',
      detail: 'Sign in instead, or reset your password if you have forgotten it.',
    };
  }
  if (m.includes('password should be at least')) {
    return {
      title: 'Password is too short',
      detail: 'Use at least 6 characters.',
    };
  }
  // Supabase puts the wait time in the message; keep it, it is the useful part.
  if (m.includes('for security purposes') || m.includes('rate limit') || m.includes('too many requests')) {
    return {
      title: 'Too many attempts',
      detail: 'Wait a moment before trying again.',
    };
  }
  if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('network request failed')) {
    return {
      title: 'No connection',
      detail: 'Check your internet and try again — nothing was submitted.',
    };
  }
  if (m.includes('unable to validate email') || m.includes('invalid email')) {
    return {
      title: 'That email address is not valid',
      detail: 'Check for typos and try again.',
    };
  }

  return { title: 'Something went wrong', detail: raw || 'Please try again.' };
}

// Inline banner, not a toast.
//
// A toast disappears after a few seconds and sits away from the field that
// caused it. On a sign-in form the error needs to stay on screen while the
// person fixes their password, so this lives in the card, above the inputs.
const AuthAlert = ({ error, onDismiss }) => {
  if (!error) return null;
  const { title, detail } = error;

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        display: 'flex',
        gap: '11px',
        alignItems: 'flex-start',
        background: c.dangerTint,
        border: '1px solid ' + c.dangerEdge,
        borderLeft: '3px solid #BE123C',
        borderRadius: radius.md,
        padding: '12px 13px',
        marginBottom: '18px',
        fontFamily: t.family,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
          background: '#BE123C', color: '#FFFFFF',
          display: 'grid', placeItems: 'center',
          fontSize: '13px', fontWeight: 800, lineHeight: 1,
        }}
      >
        !
      </span>

      <div style={{ minWidth: 0, flex: 1 }}>
        {/* #BE123C rather than the palette's danger: c.danger on dangerTint is
            only 4.39:1, under the readable floor. This clears 5.88:1. */}
        <p style={{ margin: 0, color: '#BE123C', fontSize: '13.5px', fontWeight: 700, lineHeight: 1.35 }}>
          {title}
        </p>
        {detail && (
          <p style={{ margin: '3px 0 0', color: c.inkBody, fontSize: '12.5px', lineHeight: 1.45 }}>
            {detail}
          </p>
        )}
      </div>

      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            flexShrink: 0, background: 'transparent', border: 'none',
            color: c.inkMuted, fontSize: '15px', lineHeight: 1,
            cursor: 'pointer', padding: '2px 4px',
          }}
        >
          &#10005;
        </button>
      )}
    </div>
  );
};

export default AuthAlert;